import React, { MouseEvent } from "react";
import { Link } from "react-router-dom";
import { PlanType, SubscriptionProductType } from "../../../models/plans";
import {
  formatPlanName,
  formatPrice,
} from "../../../middleware/common-functions";
import { getAuthCookie } from "../../../middleware/cookie-manager";

type PlansProps = {
  plans: PlanType[];
  selectedPlan?: SubscriptionProductType;
  handlePlanSelect?: (ev: MouseEvent<HTMLButtonElement>) => void;
  show_special_price?: boolean;
  isBuyUrl?: boolean;
  is_loyalty?: boolean;
  products: SubscriptionProductType[];
};

const Plans: React.FC<PlansProps> = ({
  plans,
  selectedPlan,
  handlePlanSelect,
  isBuyUrl,
  is_loyalty = false,
  products,
  show_special_price = false,
}) => {
  const user = getAuthCookie(true); // Force authentication since this is a protected page
  let subtitle_txt = "Purchase any of the membership packages below.";
  if (is_loyalty) {
    subtitle_txt = "Loyalty membership packages";
  }
  return (
    <div className={`${isBuyUrl ? "col-lg-12" : "col-lg-10"}`}>
      <h4 className="black_text">
        {handlePlanSelect ? subtitle_txt : "Flangoo Plans"}
      </h4>
      {handlePlanSelect ? (
        <ul className="black_text">
          <li>Select your preferred membership package and make payment.</li>
          <li>
            Subscriptions do not auto-renew. We will send you a renewal
            reminder.
          </li>
          <li>
            You can also subscribe using a purchase order on the Teacher's
            Discovery website by{" "}
            <a
              href="https://www.teachersdiscovery.com/product/29270"
              target="_blank"
              rel="noreferrer"
            >
              <u>clicking here</u>
            </a>
            {is_loyalty ? (
              <>
                {" "}
                <span>
                  and using promo code <b>LOYALTY24</b> at checkout
                </span>
              </>
            ) : (
              ""
            )}
            .
          </li>
          {isBuyUrl ? (
            ""
          ) : (
            <li>
              Renewing will ADD ON to the end of your current expiration date.
            </li>
          )}
        </ul>
      ) : null}
      {user.active && user.plan && user.plan.plan_id !== "2" ? (
        <SpecialOfferBanner
          show_special_price={show_special_price}
          handlePlanSelect={handlePlanSelect}
        />
      ) : null}
      <PricingTable
        plans={plans}
        products={products}
        is_loyalty={is_loyalty}
        handlePlanSelect={handlePlanSelect}
        selectedPlan={selectedPlan}
      />
      {user.active && user.plan && user.plan.plan_id !== "2" ? (
        <SpecialOfferBanner
          show_special_price={show_special_price}
          handlePlanSelect={handlePlanSelect}
        />
      ) : null}
    </div>
  );
};

type PricingTableProps = {
  plans: PlanType[];
  selectedPlan?: SubscriptionProductType;
  handlePlanSelect?: (ev: MouseEvent<HTMLButtonElement>) => void;
  isBuyUrl?: boolean;
  is_loyalty?: boolean;
  pupularPlanId?: string;
  free_trial?: { days: number };
  showHeader?: boolean;
  products: SubscriptionProductType[];
};

export const PricingTable: React.FC<PricingTableProps> = ({
  plans,
  products,
  is_loyalty,
  handlePlanSelect,
  selectedPlan,
  pupularPlanId,
  free_trial,
  showHeader = false,
}) => (
  <table className="table table-striped table-hover">
    {showHeader ? (
      <thead>
        <tr role="rowheader">
          <th className="empty"></th>
          {products.length > 0
            ? products.map((product) => (
                <th
                  className={`${
                    product.id === pupularPlanId ? "popular_th" : "empty"
                  }`}
                  key={`rowheader${product.id}`}
                >
                  {product.id === pupularPlanId ? "Best Value" : ""}
                </th>
              ))
            : null}
        </tr>
        <tr role="rowheader">
          <th className="empty"></th>
          {products.length > 0
            ? products.map((product) => {
                const plan = plans.find((plan) => plan.id === product.plan_id);
                if (!plan) {
                  return null;
                }
                const formatedPrice = formatPrice(product.price!);
                return (
                  <th
                    key={`plan.name${plan.id}`}
                    className={`text-center ${
                      product.id === pupularPlanId ? "popular" : ""
                    }`}
                  >
                    <h5>{formatPlanName(product.name)} </h5>
                    <small>{product.sub || "\u00A0"}</small>
                    <h3>
                      USD <br />${formatedPrice.whole}.{""}
                      <sup>{formatedPrice.decimal}</sup>
                    </h3>
                    {plan.regular_price &&
                    plan.regular_price > formatedPrice.whole ? (
                      <span className="regular_price text-success">
                        <del>${plan.regular_price}</del>
                      </span>
                    ) : (
                      <span className="regular_price opacity_0">empty</span>
                    )}

                    <Link
                      to={`/payment/${product.id}`}
                      className={`btn btn-${
                        product.id === pupularPlanId ? "" : "outline-"
                      }success btn-block mt-2 py-3`}
                    >
                      {" "}
                      Buy Now
                    </Link>
                    <small className="text-muted">
                      <i
                        className="text-success fas fa-badge-dollar"
                        aria-hidden="true"
                      ></i>{" "}
                      {free_trial?.days || "14"}-day money-back guarantee
                    </small>
                  </th>
                );
              })
            : null}
        </tr>
      </thead>
    ) : null}
    <tbody>
      {showHeader ? null : (
        <tr role="rowheader">
          <td>
            <h6>Packages</h6>
          </td>
          {products.length > 0
            ? products.map((product) => {
                const plan = plans.find((plan) => plan.id === product.plan_id);
                if (!plan) {
                  return null;
                }

                let compare_price =
                  is_loyalty && plan.loyalty_price && !plan.has_promo
                    ? plan.price_amount
                    : plan.regular_price;
                let save =
                  is_loyalty && plan.loyalty_price
                    ? compare_price - plan.loyalty_price
                    : plan.regular_price
                    ? compare_price - plan.price_amount
                    : 0;

                return (
                  <td key={`plan.name${plan.id}`} className={`text-center `}>
                    <h5>{product.name}</h5>
                    {(is_loyalty || plan.has_promo) && save > 1
                      ? `Save $${save.toFixed(2)}`
                      : ""}
                    {plan.subtitle &&
                    product.name.includes("Creator™") === false
                      ? plan.subtitle
                      : ""}
                  </td>
                );
              })
            : null}
        </tr>
      )}
      <tr>
        <td>Subscription price</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              let plan_price = is_loyalty
                ? plan.loyalty_price || plan.price_amount
                : plan.price_amount;
              let compare_price = plan_price;

              if (product.secondary_pricing) {
                plan_price += product.secondary_pricing;
              }
              if (product.secondary_pricing_regular) {
                compare_price += product.secondary_pricing_regular;
              }
              return (
                <td
                  key={`plan.price_amount${plan.id}`}
                  className={`text-center `}
                >
                  {plan.loyalty_price ? (
                    <span className="text-success">
                      USD
                      <b> ${plan.loyalty_price + product.secondary_pricing}</b>
                    </span>
                  ) : (
                    <span className="text-success">
                      USD ${plan_price.toLocaleString()}
                    </span>
                  )}
                  {plan.loyalty_price ? (
                    <>
                      <br />
                      <small className="badge text-success">
                        <i className="far fa-star" aria-hidden="true"></i>{" "}
                        Loyalty Pricing
                      </small>
                    </>
                  ) : null}
                  <br />
                  {compare_price > plan_price ? (
                    <span className="text-muted">
                      <del>${compare_price}</del>
                    </span>
                  ) : null}
                  {is_loyalty &&
                  compare_price < (plan.regular_price || plan.price_amount) &&
                  (plan.regular_price || plan.price_amount) >
                    (plan.loyalty_price || plan.price_amount) ? (
                    <span className="text-muted">
                      <del>${plan.regular_price || plan.price_amount}</del>
                    </span>
                  ) : null}
                  {!is_loyalty && plan.regular_price > plan.price_amount ? (
                    <span className="text-muted">
                      <del>${plan.regular_price || plan.price_amount}</del>
                    </span>
                  ) : null}
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>Number of student accounts per teacher</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              return (
                <td key={`plan.students${plan.id}`} className={`text-center `}>
                  {plan.students}
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>
          Access on any device—computer,
          <br />
          phone, tablet, TV
        </td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              return (
                <td className={`text-center `} key={`access_on${plan.id}`}>
                  <i className="fa fa-check" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>Integrated Quizzes</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              return (
                <td className={`text-center `} key={`languages${plan.id}`}>
                  <i className="fa fa-check" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>Audio narration</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-check" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>Use for independent or whole-class reading</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-check" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>Track students' reading activity</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-check" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>New titles added every month</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-check" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>Spanish, French, and German titles</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-check" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>
          Publish your own writing and multimedia (audio, images, videos...)
        </td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              if (product.name.includes("Creator™")) {
                return (
                  <td className={`text-center `} key={`cancel${plan.id}`}>
                    <i className="fa fa-check" aria-hidden="true"></i>
                  </td>
                );
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-times text-danger" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>
          Make content you create visible to just your own students or to all
          users
        </td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              if (product.name.includes("Creator™")) {
                return (
                  <td className={`text-center `} key={`cancel${plan.id}`}>
                    <i className="fa fa-check" aria-hidden="true"></i>
                  </td>
                );
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-times text-danger" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>Read content added by other users</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              if (product.name.includes("Creator™")) {
                return (
                  <td className={`text-center `} key={`cancel${plan.id}`}>
                    <i className="fa fa-check" aria-hidden="true"></i>
                  </td>
                );
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-times text-danger" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>Read and assign content added by other users</td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              if (product.name.includes("Creator™")) {
                return (
                  <td className={`text-center `} key={`cancel${plan.id}`}>
                    <i className="fa fa-check" aria-hidden="true"></i>
                  </td>
                );
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-times text-danger" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      <tr>
        <td>
          Create content in languages other than Spanish, French, and German
        </td>
        {products.length > 0
          ? products.map((product) => {
              const plan = plans.find((plan) => plan.id === product.plan_id);
              if (!plan) {
                return null;
              }
              if (product.name.includes("Creator™")) {
                return (
                  <td className={`text-center `} key={`cancel${plan.id}`}>
                    <i className="fa fa-check" aria-hidden="true"></i>
                  </td>
                );
              }
              return (
                <td className={`text-center `} key={`cancel${plan.id}`}>
                  <i className="fa fa-times text-danger" aria-hidden="true"></i>
                </td>
              );
            })
          : null}
      </tr>
      {handlePlanSelect ? (
        <tr>
          <td>
            <span>Select your plan:</span>
          </td>
          {products.length > 0
            ? products.map((product) => {
                const plan = plans.find((plan) => plan.id === product.plan_id);
                if (!plan) {
                  return null;
                }
                return (
                  <td className={`text-center `} key={`button${product.id}`}>
                    <button
                      className={`btn  btn-block py-3 ${
                        selectedPlan && selectedPlan.id === product.id
                          ? "btn-success"
                          : "btn-outline-dark"
                      }`}
                      onClick={handlePlanSelect}
                      data-plan-id={product.id}
                    >
                      {selectedPlan && selectedPlan.id === product.id
                        ? "Selected"
                        : "Select"}
                    </button>
                  </td>
                );
              })
            : null}
        </tr>
      ) : null}
    </tbody>
  </table>
);

const SpecialOfferBanner: React.FC<{
  show_special_price: boolean;
  handlePlanSelect?: (ev: MouseEvent<HTMLButtonElement>) => void;
}> = ({ show_special_price, handlePlanSelect }) => {
  const user = getAuthCookie(true);
  return (
    <div className="special-offer-banner">
      <span className="banner-text">
        Looking to add <b>Flangoo Creator™</b> to your current subscription?{" "}
        {user.plan?.to && show_special_price ? (
          <small>
            <br /> Get it for <b className="text_underline">$20USD</b> for the
            remainder of your subscription.
          </small>
        ) : null}
      </span>
      <button
        className="offer-button"
        onClick={handlePlanSelect}
        data-plan-id={4}
      >
        Click here
      </button>
    </div>
  );
};

export default Plans;
